// Section 7 of the campaign editor once the campaign has published.
//
// The draft review ("Review & Ready to Publish") asks whether the plan may be
// handed to a provider. A published campaign has already answered that, and
// its plan is frozen — so the same section read "Revision 2 · ready for
// publishing handoff" beside a Done button on a campaign that had been live
// for three weeks, and listed its ad sets as "differs from the local-only
// planned state required for a draft". This section says what is live and
// which checks hold, in the operator's words, and offers the two verbs a
// published campaign has: duplicate as a draft, or go back.
//
// The model is pure and exported for tests; the component is markup.

import type {
  CampaignPreflight,
  CampaignPreflightCheck,
  CampaignSyncStage,
  CampaignSyncStatus,
  CampaignSyncTarget,
} from '@/api/manage-types';
import { driftCoverageLabel } from '@/lib/provider-drift';
import { providerStatusClass } from '@/lib/campaign-publish-sequence';
import { fmtDate, fmtDay } from '@/lib/datetime';

export interface CampaignStatusSectionProps {
  target: CampaignSyncTarget;
  revision?: number;
  sync: CampaignSyncStatus | undefined;
  preflight: CampaignPreflight | undefined;
  preflightPending: boolean;
  preflightError?: string;
  deliveryCutoff?: string | null;
  /**
   * The flights are all over, or the campaign was archived (2026-10-19): the
   * facts say what ran, and a child the provider still reads ACTIVE under a
   * stopped campaign is not called live.
   */
  finished?: boolean;
  creativeName: (creativeId: string) => string;
  onDuplicate: () => void;
  onBack: () => void;
}

export interface StatusFact {
  label: string;
  value: string;
}

export interface StatusCheckLine {
  status: CampaignPreflightCheck['status'];
  subject?: string;
  message: string;
}

export interface StatusSectionModel {
  facts: StatusFact[];
  checks: StatusCheckLine[];
  /** "Every check passing · nothing waiting on you", or what is not. */
  verdict: string;
  verdictTone: 'good' | 'warn' | 'bad';
}

const PROVIDER = { meta: 'Meta', ttd: 'The Trade Desk' } as const;

/**
 * The drift scan's row is `complete` whether it found the hierarchy clean,
 * drifted, or could not see enough of it to say — the verdict is in its
 * details. Reading the status alone printed "nothing diverged" for all three
 * (audit, 2026-09-18).
 */
export function driftVerdict(drift: CampaignSyncStage): string {
  const finished = fmtDate(drift.completed_at ?? drift.updated_at);
  if (drift.status === 'failed') return `could not complete at ${fmtDate(drift.updated_at)}`;
  if (drift.status !== 'complete') return `running since ${fmtDate(drift.updated_at)}`;
  const state = drift.details?.state;
  if (state === 'clean') return `nothing diverged at ${finished}`;
  if (state === 'drifted') {
    const fields = drift.details?.diverged_fields ?? 0;
    return `${fields} field${fields === 1 ? '' : 's'} diverged at ${finished}`;
  }
  if (state === 'incomplete') return `could not see the whole hierarchy at ${finished}, so no verdict`;
  return `finished at ${finished}, verdict not recorded`;
}

export function statusSectionModel(
  props: Pick<CampaignStatusSectionProps, 'target' | 'revision' | 'sync' | 'preflight' | 'deliveryCutoff' | 'finished' | 'creativeName'>,
): StatusSectionModel {
  const { target, revision, sync, preflight, deliveryCutoff, finished, creativeName } = props;
  const provider = PROVIDER[target];
  const facts: StatusFact[] = [];
  const stages = (sync?.stages ?? []).filter((stage) => stage.target === target);
  const publish = stages.find((stage) => stage.stage_key === 'publish');
  const publishedAt = publish?.completed_at ?? sync?.[target]?.last_synced_at;
  facts.push({
    label: 'Published',
    value: `${publishedAt ? fmtDate(publishedAt) : 'not yet confirmed'}${revision ? ` · revision ${revision}` : ''}`,
  });
  const externalId = sync?.[target]?.external_id;
  facts.push({
    label: `${provider} campaign`,
    value: externalId ?? 'no provider id recorded',
  });
  const groups = (sync?.delivery_groups ?? []).filter((group) => group.target === target);
  if (groups.length > 0) {
    const classes = groups.map((group) => providerStatusClass(group.provider_status, group.provider_delivery_group_id));
    const live = groups.filter((_, index) => classes[index] === 'active');
    const paused = classes.filter((cls) => cls === 'paused').length;
    const attention = classes.filter((cls) => cls === 'attention').length;
    const audiences = (rows: typeof groups) => rows.map((group) => `Audience ${group.audience_segment_position + 1}`).join(', ');
    facts.push({
      label: target === 'meta' ? 'Ad sets' : 'Ad groups',
      value: finished
        ? `${groups.length} · ${audiences(groups)}${attention ? ` · ${attention} needs review` : ''}`
        : [
            `${live.length} live${live.length ? ` · ${audiences(live)}` : ''}`,
            paused ? `${paused} paused` : '',
            attention ? `${attention} needs review` : '',
          ].filter(Boolean).join(' · '),
    });
  }
  const ads = (sync?.ads ?? []).filter((ad) => ad.target === target);
  if (target === 'meta' && ads.length > 0) {
    const classes = ads.map((ad) => providerStatusClass(ad.provider_status, ad.provider_ad_id));
    const active = classes.filter((cls) => cls === 'active').length;
    const paused = classes.filter((cls) => cls === 'paused').length;
    const attention = classes.filter((cls) => cls === 'attention').length;
    const creatives = new Set(ads.map((ad) => ad.creative_position)).size;
    const grid = `${creatives} creative${creatives === 1 ? '' : 's'} × ${groups.length} ad set${groups.length === 1 ? '' : 's'}`;
    facts.push({
      label: 'Ads',
      value: finished
        ? `${ads.length}${attention ? ` · ${attention} needs review` : ''} · ${grid}`
        : `${active} active${paused ? ` · ${paused} paused` : ''}${attention ? ` · ${attention} needs review` : ''} · ${grid}`,
    });
  }
  const conversion = preflight?.checks.find((check) =>
    check.code === (target === 'meta' ? 'meta_custom_conversion' : 'ttd_conversion_reporting'));
  if (conversion) facts.push({ label: 'Conversions', value: conversion.message });
  facts.push({
    label: 'Last report',
    value: deliveryCutoff ? `delivery complete through ${fmtDay(deliveryCutoff)}` : 'no complete delivery day yet',
  });
  // Coverage lists every campaign that has provider objects to verify. A draft
  // has none, and "Status unverified" on it would claim a check that was never
  // owed.
  const drift = sync?.provider_drift?.find((scan) => scan.target === target);
  if (drift) facts.push({ label: 'Drift check', value: driftCoverageLabel(drift) });

  const checks: StatusCheckLine[] = (preflight?.checks ?? []).map((check) => ({
    status: check.status,
    subject: check.segment_position !== undefined
      ? `Audience ${check.segment_position + 1}`
      : check.creative_id
        ? creativeName(check.creative_id)
        : undefined,
    message: check.message,
  }));
  const failing = checks.filter((check) => check.status === 'fail').length;
  const notes = checks.filter((check) => check.status === 'info').length;
  const verdict = !preflight
    ? 'Checks not read yet'
    : failing > 0
      ? `${failing} check${failing === 1 ? '' : 's'} failing`
      : notes > 0
        ? `Every check passing · ${notes} note${notes === 1 ? '' : 's'}, nothing waiting on you`
        : 'Every check passing · nothing waiting on you';
  return {
    facts,
    checks,
    verdict,
    verdictTone: !preflight ? 'warn' : failing > 0 ? 'bad' : 'good',
  };
}

const GLYPH = { pass: '✓', info: 'i', fail: '✗' } as const;
const GLYPH_COLOR = { pass: 'var(--good)', info: 'var(--accent)', fail: 'var(--bad)' } as const;
const VERDICT_COLOR = { good: 'var(--good)', warn: 'var(--ink-2)', bad: 'var(--bad)' } as const;

export function CampaignStatusSection(props: CampaignStatusSectionProps) {
  const model = statusSectionModel(props);
  const { preflightPending, preflightError, finished, onDuplicate, onBack } = props;
  return (
    <div className="campaign-status-section">
      <div className="grid-2 mb-3 campaign-status-columns">
        <div>
          <div className="field-lbl mb-2">{finished ? 'What ran' : 'What is live'}</div>
          {model.facts.map((fact) => (
            <div key={fact.label} className="campaign-status-fact">
              <span className="muted mono campaign-status-fact-label">{fact.label}</span>
              <b>{fact.value}</b>
            </div>
          ))}
        </div>
        <div>
          <div className="field-lbl mb-2">Checks</div>
          {preflightPending && model.checks.length === 0 && (
            <div className="muted" style={{ fontSize: 11.5 }}>Checking the published plan…</div>
          )}
          {preflightError && model.checks.length === 0 && (
            <div style={{ fontSize: 11.5, color: 'var(--bad)' }}>{preflightError}</div>
          )}
          {model.checks.map((check, index) => (
            <div key={`${check.subject ?? ''}-${index}`} className="campaign-status-check">
              <span className="mono" style={{ color: GLYPH_COLOR[check.status] }} aria-label={check.status}>
                {GLYPH[check.status]}
              </span>
              <span>{check.subject && <b>{check.subject} · </b>}{check.message}</span>
            </div>
          ))}
          <div className="campaign-status-verdict" style={{ color: VERDICT_COLOR[model.verdictTone] }}>
            {model.verdict}
          </div>
        </div>
      </div>
      <div className="campaign-status-foot">
        <div className="muted" style={{ fontSize: 11.5 }}>
          Audience, channel, schedule and budget are frozen at publish. To change them, duplicate this
          campaign as a new draft. Creatives change in section 4.
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="btn" onClick={onDuplicate}>Duplicate as draft</button>
          <button type="button" className="btn btn-ghost" onClick={onBack}>Back to campaigns</button>
        </div>
      </div>
    </div>
  );
}
